import React, { Fragment, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Splitter } from "@progress/kendo-react-layout";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import {
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
  ToolbarSpacer,
  Button,
  DropDownButton,
} from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { DropDownListById } from "../../forms/form-components";
import { Form } from "../../components/form";
import { Checkbox, Input, TextArea } from "@progress/kendo-react-inputs";
import { List } from "../../components/list";
import { ClaimsGrid, SecurityGroupsPageForm } from "./securitygroupspageform";
import {
  canCreateUsers,
  canDeleteUsers,
  canEditUsers,
  canViewUsers
} from "../../routing/authorizationhandlers";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const toolbarRenderer = (props) => {
  const { currentTheme, currentUser, pageState, t } = props;

  const saveTitle =
    pageState.mode == 1 ? t("securitygroups.save") : t("securitygroups.update");

  const toolsVisibleIcon = pageState.toolsVisible ? "unpin" : "pin";
  const toolsVisibleTitle = pageState.toolsVisible
    ? t("securitygroups.hide")
    : t("securitygroups.show");

  return (
    <Toolbar>
      <ToolbarItem>
        <Button
          disabled={pageState.processing}
          style={{ color: currentTheme.primary }}
          icon={toolsVisibleIcon}
          onClick={props.onToolsToggle}
        >
          {toolsVisibleTitle}
        </Button>
      </ToolbarItem>
      <ToolbarSeparator />
      {currentUser.IsAuthenticated && canCreateUsers(currentUser) && (
        <ToolbarItem>
          <Button
            disabled={pageState.processing}
            style={{ color: currentTheme.primary }}
            icon="plus"
            onClick={props.onNew}
          >
            {t("securitygroups.create")}
          </Button>
        </ToolbarItem>
      )}
      {!pageState.formValues.IsSystem &&
        ((pageState.mode == 1 &&
          currentUser.IsAuthenticated &&
          canCreateUsers(currentUser)) ||
          (pageState.mode == 2 &&
            currentUser.IsAuthenticated &&
            canEditUsers(currentUser))) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={pageState.processing}
                style={{ color: currentTheme.primary }}
                icon="save"
                onClick={props.onSave}
              >
                {saveTitle}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
      {pageState.mode == 2 &&
        currentUser.IsAuthenticated &&
        canDeleteUsers(currentUser) &&
        !pageState.formValues.IsSystem && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={pageState.processing}
                style={{ color: currentTheme.primary }}
                icon="delete"
                onClick={(e) => props.onDelete(null)}
              >
                {t("securitygroups.deletegrp")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
    </Toolbar>
  );
};

const getEntityOperations = (entity, currentUser, t) => {
  let result = [];

  if (currentUser.IsAuthenticated && (canViewUsers(currentUser) || canEditUsers(currentUser)))
    result.push({ id: 2, text: t("securitygroups.view"), icon: "eye" });

  if (currentUser.IsAuthenticated && canDeleteUsers(currentUser))
    result.push({ id: 3, text: t("securitygroups.delete"), icon: "delete" });

  return result;
};

const entityMenuItemRenderer =
  (currentTheme, entity, onItemClicked) => (props) => {
    let isDisabled = false;

    if (
      props.item.id == 3 &&
      (entity.IsFederationRoot || entity?.IsRootSubscription)
    )
      isDisabled = true;

    if (props.item.text)
      return (
        <div
          style={{ display: "flex", width: "100%", padding: "0px 6px" }}
          onClick={(e) => onItemClicked(entity.Id, props.item)}
        >
          <span
            className={`k-icon k-i-${props.item.icon}`}
            style={{ display: "inline-block" }}
          ></span>
          <div style={{ marginLeft: 12 }}>{props.item.text}</div>
        </div>
      );
    else return <div className="menu-separator" style={{}} />;
  };

const entitiesItemRenderer =
  (currentTheme, currentUser, t) => (props, item, index) => {
    let icon = "k-i-group";

    return (
      <div
        key={item.Id}
        className={`list-item`}
        style={{}}
        onDoubleClick={(e) => {
          if (currentUser.IsAuthenticated && (canViewUsers(currentUser) || canEditUsers(currentUser)))
            props.onEntityDoubleClick(item.Id);
        }}
      >
        <div className={`list-item-icon k-icon ${icon} `}></div>
        <div className="list-item-title">{item.Name}</div>
        {currentUser.IsAuthenticated &&
          (canViewUsers(currentUser) || canEditUsers(currentUser) || canDeleteUsers(currentUser)) && (
            <div className="list-item-operations">
              <DropDownButton
                className="buttons-container-button"
                items={getEntityOperations(item, currentUser, t)}
                itemRender={entityMenuItemRenderer(
                  currentTheme,
                  item,
                  (entityId, _item) => {
                    if (_item.id == 1 || _item.id == 2)
                      //view
                      props.onEntitySelected(entityId, _item);
                    else if (_item.id == 3)
                      // delete
                      props.onDelete(entityId, _item);
                  }
                )}
                icon="more-horizontal"
                fillMode={"flat"}
              />
            </div>
          )}
      </div>
    );
  };

const MobileSecurityGroups = ({ currentTheme, currentUser, pageState, t, ...props }) => {
  const [entityFilter, setEntityFilter] = useState("");
  const group = pageState.formValues;
  const filteredGroups = pageState.entities.filter((entity) => {
    if (!entityFilter.trim()) return true;

    return entity.Name.toLowerCase().includes(entityFilter.toLowerCase());
  });

  if (pageState.mode == 2 && group.Id) {
    return (
      <div className="securitygroups-mobile-view securitygroups-mobile-details">
        <Button
          fillMode="flat"
          icon="arrow-60-left"
          className="securitygroups-mobile-back"
          onClick={props.onClose}
        >
          {t("users.close")}
        </Button>
        <div className="securitygroups-mobile-details-form">
          <div className="securitygroups-mobile-field">
            <label htmlFor="mobile-securitygroup-name">{t("securitygroups.name")}</label>
            <Input id="mobile-securitygroup-name" value={group.Name || ""} disabled />
          </div>
          <div className="securitygroups-mobile-field">
            <label htmlFor="mobile-securitygroup-description">
              {t("securitygroups.description")}
            </label>
            <TextArea
              id="mobile-securitygroup-description"
              value={group.Description || ""}
              disabled
              rows={2}
            />
          </div>
          <div className="securitygroups-mobile-field">
            <label>{t("securitygroups.permissions")}</label>
            <div className="securitygroups-mobile-permissions">
              <ClaimsGrid claims={group.Claims || []} onToggleClaim={() => {}} t={t} disabled />
            </div>
          </div>
          <div className="securitygroups-mobile-field">
            <label>{t("securitygroups.users")}</label>
            <div className="securitygroups-mobile-checkbox-list">
              {(group.Users || []).map((user) => (
                <Checkbox key={user.Id} checked={!!user.Checked} label={user.Name} disabled />
              ))}
            </div>
          </div>
          <div className="securitygroups-mobile-field">
            <label>{t("securitygroups.reports")}</label>
            <div className="securitygroups-mobile-checkbox-list">
              {(group.Reports || []).map((report) => (
                <Checkbox key={report.Id} checked={!!report.Checked} label={report.Title} disabled />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="securitygroups-mobile-view">
      <div className="securitygroups-mobile-heading">
        <h1>{t("securitygroups.groups")}</h1>
      </div>
      <Input
        type="search"
        value={entityFilter}
        onChange={(event) => setEntityFilter(event.value)}
        placeholder={t("securitygroups.search")}
        className="securitygroups-mobile-search"
      />
      <List
        className="securitygroups-mobile-list"
        items={filteredGroups}
        itemRenderer={entitiesItemRenderer(currentTheme, currentUser, t)}
        onEntitySelected={props.onEntitySelected}
        onEntityDoubleClick={props.onEntitySelected}
        onDelete={props.onDelete}
      />
    </div>
  );
};

export const SecurityGroupsPageBody = (props) => {
  const {
    onSplitterChange,
    onNew,
    onSave,
    onDelete,
    onErrorbarClose,
    onToolsToggle,
    onEntitySelected,
    onEntityDoubleClick,
    updateDirtyState,
    onToggleClaim,
    onSelectAllClaims,
    onToggleAllReports,
    ...other
  } = props;

  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.securityGroupsPage);
  const appearance = useSelector((state) => state.appearance);
  const entities = pageState.entities;
  const [entityFilter, setEntityFilter] = useState("");

  const { t } = useTranslation();
  const getPanelBarItems = () => {
    const panelBarItems = [];

    panelBarItems.push(
      <PanelBarItem
        className="panel-entities"
        key="entities"
        title={t("securitygroups.groups")}
        expanded={true}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Input
            type="search"
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.value)}
            style={{
              width: "calc(100% - 16px)",
              margin: "8px 8px 8px 8px",
              backgroundColor: "transparent",
            }}
            placeholder={t("securitygroups.search")}
          />
          <List
            className="entities"
            items={entities.filter((e) => {
              if (!entityFilter || entityFilter.trim() == "") return true;

              const filter = entityFilter.toLowerCase();
              return e.Name.toLowerCase().indexOf(filter) >= 0;
            })}
            itemRenderer={entitiesItemRenderer(currentTheme, currentUser, t)}
            onEntitySelected={onEntitySelected}
            onEntityDoubleClick={onEntityDoubleClick}
            onDelete={props.onDelete}
          />
        </div>
      </PanelBarItem>
    );

    return panelBarItems;
  };

  return (
    <Fragment>
      {pageState.processing && (
        <div className="page-loader">
          <Loader
            size={"large"}
            type="infinite-spinner"
            themeColor={"primary"}
          />
          {pageState.processingText && (
            <div style={{ fontSize: 13, color: currentTheme.primary }}>
              {pageState.processingText}
            </div>
          )}
        </div>
      )}
      {pageState.pageLoaded && (
        <Fragment>
          <div className="securitygroups-desktop-view">
            <Splitter
              style={{ border: "none" }}
              panes={pageState.panes}
              onChange={onSplitterChange}
            >
              <div className="securitygroups-page-manager">
                <PanelBar style={{ border: "none" }} expanded={[".0"]}>
                  {getPanelBarItems()}
                </PanelBar>
              </div>

              <Form
                className="securitygroups-page-form"
                currentTheme={currentTheme}
                currentUser={currentUser}
                toolbarRenderer={toolbarRenderer}
                t={t}
                onNew={onNew}
                onSave={onSave}
                onDelete={onDelete}
                errorMessages={pageState.formServerErrors}
                onErrorbarClose={onErrorbarClose}
                pageState={pageState}
                onToolsToggle={onToolsToggle}
              >
                {pageState.mode > 0 ? (
                  <SecurityGroupsPageForm
                    onUserCheck={props.onUserCheck}
                    onReportCheck={props.onReportCheck}
                    updateDirtyState={updateDirtyState}
                    onToggleClaim={onToggleClaim}
                    onSelectAllClaims={onSelectAllClaims}
                    onToggleAllReports={onToggleAllReports}
                  />
                ) : (
                  <Fragment></Fragment>
                )}
              </Form>
            </Splitter>
          </div>
          <MobileSecurityGroups
            currentTheme={currentTheme}
            currentUser={currentUser}
            pageState={pageState}
            t={t}
            onEntitySelected={onEntitySelected}
            onDelete={onDelete}
            onClose={props.onMobileClose}
          />
        </Fragment>
      )}

      <style>
        {`                  
                    .securitygroups-page > .content
                    {
                      
                    }

                    .securitygroups-wrapper{ 
                        direction:${
                          appearance.isRightToLeft ? "rtl" : "ltr"
                        }                      
                    }    
                    .securitygroups-page .k-splitter{
                      flex-direction:${
                        appearance.isRightToLeft ? "row-reverse" : "row"
                      } 
                    }
                      
                    .securitygroups-page .k-splitter .k-pane{
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                    }

                    .securitygroups-page > .content .securitygroups-page-manager
                    {
                      border: ${currentTheme.managerBorder};
                      border-radius: ${currentTheme.pageBorderRadius};
                      background-color: ${currentTheme.componentBgFaded};
                    }                

                    .securitygroups-page > .content .securitygroups-page-manager .k-panelbar
                    {
                      /*required*/
                      background-color: transparent;
                    }

                    .securitygroups-page > .content .securitygroups-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link .k-icon
                    {
                      /*the arrow near every panel header*/
                      color: ${currentTheme.touchColor}!important;
                      margin-right: 6px;
                    }

                    .securitygroups-page > .content .securitygroups-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link
                    {
                      /*the span in the above <li> i.e. the title of every group in the manager: datasets, securitygroups, columns and description*/  
                      background-color: transparent;
                      color: ${currentTheme.touchColor};
                      border-bottom: 1px solid ${currentTheme.separatorColor}; 
                    }

                    .securitygroups-page > .content .securitygroups-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link:hover
                    {
                      /*the above hovered - do nothing*/
                    }

                    .securitygroups-page > .content .securitygroups-page-manager .panel-entities .list-item:hover
                    {
                      /*expression item hover */
                      background-color: ${currentTheme.componentBorderColor};
                      cursor: pointer;
                    }

                    .securitygroups-page > .content .securitygroups-page-manager .panel-entities .k-input::placeholder
                    {
                        color: ${currentTheme.componentText};
                        /*font-style: italic;*/
                    }
                   
                    .securitygroups-page > .content .securitygroups-page-manager .list-container
                    {
                      /*bg of both the exp. and columns Lists*/
                      background-color: transparent;
                      padding : 0;/*can add padding to list items*/
                    }

                    .securitygroups-page > .content .securitygroups-page-manager .k-panelbar-header
                    {
                      background-color: transparent;
                    }

                    .securitygroups-page > .content .securitygroups-page-manager .k-panelbar-content
                    {
                      background-color: transparent;
                    }

                    .securitygroups-page > .content .securitygroups-page-manager .entities .list-item-icon
                    {
                      /*first icon in securitygroups list*/
                      padding-left: 12px
                    }                   

                    .securitygroups-page > .content .securitygroups-page-manager .entities .list-item-title
                    {
                      vertical-align: middle;/*fine tuning*/
                    }                    

                    /*the form*/
                    .securitygroups-page-form   
                    {
                        background-color: transparent;
                        padding-left: 0px;
                        border: ${currentTheme.pageFormBorder};   
                        border-radius: ${currentTheme.pageBorderRadius};
                    }

                    /*form toolbar*/
                    .securitygroups-page-form > .toolbar .k-toolbar
                    {
                        width: 100%;
                        border-left: none !important;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        border-top: none !important;
                        border-right: none !important;
                        background-color: ${
                          currentTheme.toolbarBg
                        };/*transparent;*/
                        direction:${appearance.isRightToLeft ? "rtl" : "ltr"}
                    }

                    .securitygroups-page > .content .toolbar .k-button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                    }

                    .securitygroups-page > .content .toolbar .k-button-text
                    {
                      /*color of the text in every toolbar button=black in azure theme*/
                      color: ${currentTheme.toolbarText};
                    }

                    .securitygroups-page > .content .toolbar .k-button-icon
                    {
                      font-weight: 500;
                      color: ${currentTheme.primary};
                    }

                    .securitygroups-page > .content .form-root .k-form .k-form-field:nth-child(1)
                    {
                        margin-top: 4px;
                    }

                    .securitygroups-page > .content .form-root .k-form .k-form-field
                    {
                        margin-top: 32px;
                    }

                    /*.securitygroups-page > .content .form-root .k-form .k-form-field:nth-child(6)                   
                    {
                        margin-top: -4px !important;
                        width: 100%;
                    }*/

                    .securitygroups-page > .content .form-root .content
                    {
                      background-color: ${currentTheme.pageContentBg};
                    }

                    /*styling the popup menu */
                    .k-popup ul{
                        padding: 0px;
                    }

                    .k-popup ul > li{
                        margin: 0;
                        padding: 0px;                                
                    }

                    .k-popup ul > li > span{
                        border: none !important;
                        box-shadow: none !important;
                    }

                    .k-menu-link{
                        padding: 0px 0px !important;
                        
                    }

                    .securitygroups-desktop-view {
                      flex: 0 0 100%;
                      width: 100%;
                      min-width: 0;
                      height: 100%;
                    }

                    .securitygroups-desktop-view .k-splitter {
                      width: 100%;
                      height: 100%;
                    }

                    .securitygroups-mobile-view {
                      display: none;
                    }

                    @media (max-width: 1024px) {
                      .securitygroups-page > .content {
                        overflow-y: auto;
                      }

                      .securitygroups-desktop-view {
                        display: none;
                      }

                      .securitygroups-mobile-view {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        min-height: 100%;
                        padding: 18px 16px;
                        box-sizing: border-box;
                        background: ${currentTheme.componentBg};
                        color: ${currentTheme.componentText};
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"};
                      }

                      .securitygroups-mobile-heading h1 {
                        margin: 0 0 18px;
                        color: ${currentTheme.componentText};
                        font-size: 21px;
                        font-weight: 600;
                      }

                      .securitygroups-page .securitygroups-mobile-search {
                        margin-bottom: 12px;
                        background: transparent;
                      }

                      .securitygroups-mobile-list,
                      .securitygroups-mobile-list .list-root {
                        width: 100%;
                        box-sizing: border-box;
                      }

                      .securitygroups-mobile-list .list-root,
                      .securitygroups-mobile-list .list-root > div,
                      .securitygroups-mobile-list .list-root > div > div {
                        display: block;
                      }

                      .securitygroups-mobile-list .list-root > div > div {
                        padding: 0;
                      }

                      .securitygroups-page .securitygroups-mobile-list .list-item {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        padding: 12px 0;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        color: ${currentTheme.componentText};
                      }

                      .securitygroups-page .securitygroups-mobile-list .list-item-icon {
                        width: 24px;
                        color: ${currentTheme.primary};
                        font-size: 17px;
                      }

                      .securitygroups-page .securitygroups-mobile-list .list-item-title {
                        flex: 1;
                        min-width: 0;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        font-size: 14px;
                        font-weight: 600;
                      }

                      .securitygroups-page .securitygroups-mobile-list .list-item-operations {
                        flex: 0 0 auto;
                      }

                      .securitygroups-page .securitygroups-mobile-list .buttons-container-button {
                        min-width: auto;
                        padding: 4px;
                        color: ${currentTheme.primary};
                      }

                      .securitygroups-page .securitygroups-mobile-details {
                        padding-top: 10px;
                      }

                      .securitygroups-page .securitygroups-mobile-back {
                        align-self: flex-start;
                        margin-bottom: 18px;
                        color: ${currentTheme.primary};
                      }

                      .securitygroups-mobile-details-form {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        gap: 16px;
                        box-sizing: border-box;
                      }

                      .securitygroups-mobile-field {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                      }

                      .securitygroups-mobile-field > label {
                        font-size: 12px;
                        color: ${currentTheme.inputLabelColor};
                      }

                      .securitygroups-mobile-details-form input.k-input,
                      .securitygroups-mobile-details-form textarea.k-textarea {
                        padding: 6px 8px;
                      }

                      .securitygroups-mobile-permissions {
                        width: 100%;
                        overflow-x: auto;
                      }

                      .securitygroups-mobile-checkbox-list {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 10px 16px;
                        padding: 10px;
                        border: 1px solid ${currentTheme.componentBorderColor};
                        border-radius: 4px;
                      }
                    }
                  `}
      </style>
    </Fragment>
  );
};
